// TeamView: Slim coordinator component for the team overview
// Orchestrates data loading (useTeamViewData), filter management (useTeamViewFilters),
// conflict detection (useConflictDetection), and task actions (useTeamViewTaskActions)
// Delegates rendering to TeamViewDesktopLayout and TeamViewMobileLayout
// REFACTORED: Decomposed from 1056-line monolith into coordinator + 2 hooks + 2 layout components
// FIX: Added rollback call on TaskEditModal cancel to revert drag-drop visual state
// UPDATED: Wired up useTeamSchedulePrint for PDF export via onPrint/isPrinting props
// PERF: Conflict detection now reuses tasks loaded by the execution layer (no double-fetch)
// FIX: operatorName now uses assigned_to_user.full_name (proper DB field) instead of getFullName
// UPDATED: Added list view (agenda) with week/month period navigation
// UPDATED: Added PDF export for list view via useAgendaListPrint; onPrint unified across views
// FIX: Changed desktop container from h-screen to h-[calc(100vh-4rem)] to match py-8 parent padding
//      and eliminate the outer page scroll bleed
// PERF: viewMode and listPeriodType now use lazy useState initializers to avoid flash of default state
// FIX: Added useEffect keyed on user?.id to re-read preferences on user switch (avoids state leak across sessions)

import { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useTeamViewFilters } from '../../hooks/useTeamViewFilters';
import { useConflictDetection } from '../../hooks/useConflictDetection';
import { useTeamViewData } from '../../hooks/useTeamViewData';
import { useTeamViewTaskActions } from '../../hooks/useTeamViewTaskActions';
import { useTeamSchedulePrint } from '../../hooks/useTeamSchedulePrint';
import { useUpcomingMissions } from '../../hooks/useUpcomingMissions';
import { useAgendaListPrint } from '../../hooks/useAgendaListPrint';
import { getTeamViewPreferences } from '../../lib/teamViewPreferences';
import { TaskDetailModal } from '../manager/TaskDetailModal';
import { TaskEditModal } from '../manager/TaskEditModal';
import { DeleteTaskModal } from '../manager/DeleteTaskModal';
import { TeamViewDesktopLayout } from './TeamViewDesktopLayout';
import { TeamViewMobileLayout } from './TeamViewMobileLayout';
import { getPeriodBounds, formatPeriodLabel } from '../manager/agenda/PeriodNavigator';
import type { PeriodType } from '../manager/agenda/PeriodNavigator';

export function TeamView() {
  const { user } = useAuth();
  const { isDesktop } = useResponsiveLayout();

  const [viewMode, setViewMode] = useState<'calendar' | 'list'>(() => {
    if (!user?.id) return 'calendar';
    const prefs = getTeamViewPreferences(user.id);
    return prefs?.viewMode || 'calendar';
  });
  const [listPeriodType, setListPeriodType] = useState<PeriodType>(() => {
    if (!user?.id) return 'week';
    const prefs = getTeamViewPreferences(user.id);
    return prefs?.listPeriodType || 'week';
  });
  const [listReferenceDate, setListReferenceDate] = useState<Date>(() => new Date());

  useEffect(() => {
    if (user?.id) {
      const prefs = getTeamViewPreferences(user.id);
      setViewMode(prefs?.viewMode || 'calendar');
      setListPeriodType(prefs?.listPeriodType || 'week');
      setListReferenceDate(new Date());
    } else {
      setViewMode('calendar');
      setListPeriodType('week');
      setListReferenceDate(new Date());
    }
  }, [user?.id]);

  const data = useTeamViewData({
    userId: user?.id || null,
    userRole: user?.role || null,
  });

  const filters = useTeamViewFilters({
    userId: user?.id || null,
    sites: data.sites,
    services: data.services,
    allProfilesForSite: data.allProfilesForSite,
    allTasksForSite: data.allTasksForSite,
    onScrollTrigger: data.triggerScroll,
    viewMode,
    listPeriodType,
  });

  const {
    activeConflicts,
    loading: conflictsLoading,
    refresh: refreshConflicts,
  } = useConflictDetection(filters.selectedSiteId, data.selectedDate, data.allTasksForSite);

  useEffect(() => {
    if (filters.selectedSiteId) {
      filters.setSelectedProfileIds([]);
      data.loadProfiles(filters.selectedSiteId);
      data.clearTasks();
    }
  }, [filters.selectedSiteId]);

  useEffect(() => {
    if (filters.selectedSiteId) {
      data.loadTasks(filters.selectedSiteId);
    }
  }, [filters.selectedSiteId, data.selectedDate]);

  const reloadTasks = async (isManualRefresh = false) => {
    if (!filters.selectedSiteId) return;
    await data.loadTasks(filters.selectedSiteId, isManualRefresh);
  };

  const handleManualRefresh = async () => {
    await reloadTasks(true);
    data.loadServices();
    data.triggerScroll();
  };

  const { start: periodStart, end: periodEnd } = useMemo(
    () => getPeriodBounds(listReferenceDate, listPeriodType),
    [listReferenceDate, listPeriodType]
  );

  const {
    tasks: allListTasks,
    loading: listLoading,
    error: listError,
    isRefreshing: listIsRefreshing,
    refresh: refreshListTasks,
  } = useUpcomingMissions({
    userId: user?.id || null,
    userRole: user?.role || null,
    siteId: filters.selectedSiteId,
    periodStart: viewMode === 'list' ? periodStart : null,
    periodEnd: viewMode === 'list' ? periodEnd : null,
  });

  const listTasks = useMemo(() => {
    if (filters.selectedServiceIds.length === 0 || filters.selectedProfileIds.length === 0) return [];
    const allSelected = filters.selectedServiceIds.length === data.services.length && data.services.length > 0;
    const byService = allSelected
      ? allListTasks
      : allListTasks.filter(t => t.template_service_id && filters.selectedServiceIds.includes(t.template_service_id));
    return byService.filter(t => filters.selectedProfileIds.includes(t.assigned_to_user_id));
  }, [allListTasks, filters.selectedServiceIds, filters.selectedProfileIds, data.services.length]);

  const handleViewModeChange = useCallback((mode: 'calendar' | 'list') => {
    setViewMode(mode);
  }, []);

  const handleListNavigate = useCallback((date: Date) => {
    setListReferenceDate(date);
  }, []);

  const handleListPeriodTypeChange = useCallback((type: PeriodType) => {
    setListPeriodType(type);
  }, []);

  const tasks = useMemo(() => {
    if (filters.selectedServiceIds.length === 0) return [];

    const allSelected = filters.selectedServiceIds.length === data.services.length && data.services.length > 0;
    const baseTasks = allSelected
      ? data.allTasksForSite
      : data.allTasksForSite.filter(task => task.template_service_id && filters.selectedServiceIds.includes(task.template_service_id));

    return baseTasks.filter(task => filters.selectedProfileIds.includes(task.assigned_to_user_id));
  }, [filters.selectedServiceIds, data.allTasksForSite, filters.selectedProfileIds, data.services.length]);

  const timelineTasks = useMemo(() => {
    return tasks.map(task => {
      const service = data.services.find(s => s.id === task.template_service_id);
      const operatorName = task.assigned_to_user?.full_name || 'Opérateur';
      return {
        id: task.id,
        title: task.template.name,
        startTime: task.scheduled_time || '09:00',
        duration: task.duration_override_minutes || task.template.estimated_duration_minutes,
        status: task.status,
        operatorName,
        operatorUsername: task.assigned_to_user?.username || 'unknown',
        operatorId: task.assigned_to_user_id,
        isRecurring: task.is_recurring,
        parentTaskId: task.parentTaskId,
        occurrenceDate: task.occurrenceDate,
        serviceColor: service?.color,
        serviceName: service?.name,
      };
    });
  }, [tasks, data.services]);

  const conflictingTaskIds = useMemo(() => {
    const ids = new Set<string>();
    activeConflicts.forEach(conflict => {
      ids.add(conflict.task1.id);
      ids.add(conflict.task2.id);
    });
    return ids;
  }, [activeConflicts]);

  const displayedOperators = useMemo(() => {
    return data.allProfilesForSite.filter(p => filters.selectedProfileIds.includes(p.id));
  }, [data.allProfilesForSite, filters.selectedProfileIds]);

  const selectedSite = useMemo(() => {
    return data.sites.find(s => s.id === filters.selectedSiteId) || null;
  }, [data.sites, filters.selectedSiteId]);

  const { handlePrint: handleCalendarPrint, isPrinting: isCalendarPrinting } = useTeamSchedulePrint({
    siteName: selectedSite?.name || '',
    date: data.dateObject,
    displayedOperators,
    tasks: timelineTasks,
  });

  const periodLabel = useMemo(
    () => formatPeriodLabel(listReferenceDate, listPeriodType),
    [listReferenceDate, listPeriodType]
  );

  const { handlePrint: handleListPrint, isPrinting: isListPrinting } = useAgendaListPrint({
    siteName: selectedSite?.name || '',
    periodLabel,
    tasks: listTasks,
    services: data.services,
  });

  const handlePrint = viewMode === 'list' ? handleListPrint : handleCalendarPrint;
  const isPrinting = isCalendarPrinting || isListPrinting;

  const taskActions = useTeamViewTaskActions({
    user: user ? { id: user.id, role: user.role } : null,
    tasks,
    allProfilesForSite: data.allProfilesForSite,
    services: data.services,
    reloadTasks: () => reloadTasks(),
  });

  const handleConflictClick = (userId: string, _time: string, serviceId1: string | null, serviceId2: string | null) => {
    const serviceIdsToSelect = new Set<string>();
    if (serviceId1) serviceIdsToSelect.add(serviceId1);
    if (serviceId2) serviceIdsToSelect.add(serviceId2);

    if (serviceIdsToSelect.size > 0) {
      if (filters.selectedServiceIds.length === 0) {
        filters.setSelectedServiceIds(Array.from(serviceIdsToSelect));
      } else {
        const newSelection = [...filters.selectedServiceIds];
        serviceIdsToSelect.forEach(id => {
          if (!newSelection.includes(id)) newSelection.push(id);
        });
        filters.setSelectedServiceIds(newSelection);
      }
    }

    if (!filters.selectedProfileIds.includes(userId)) {
      filters.setSelectedProfileIds([...filters.selectedProfileIds, userId]);
    }

    data.triggerScroll();
  };

  if (!user) return null;

  if (data.loading && data.sites.length === 0) {
    return (
      <div className="pb-20">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </div>
    );
  }

  if (data.error && data.sites.length === 0) {
    return (
      <div className="pb-20">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-gray-600">{data.error}</p>
          <button
            onClick={data.loadSites}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (data.sites.length === 0) {
    return (
      <div className="pb-20">
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">Aucun site assigné</p>
        </div>
      </div>
    );
  }

  const sharedLayoutProps = {
    sites: data.sites,
    services: data.services,
    selectedSiteId: filters.selectedSiteId,
    selectedServiceIds: filters.selectedServiceIds,
    selectedProfileIds: filters.selectedProfileIds,
    selectionMode: filters.selectionMode,
    onSiteChange: filters.handleSiteChange,
    onServiceChange: filters.handleServiceChange,
    onOperatorSelectionChange: filters.handleOperatorSelectionChange,
    onSelectionModeChange: filters.setSelectionMode,
    allProfilesForSite: data.allProfilesForSite,
    loading: data.loading,
    error: data.error,
    isRefreshing: data.isRefreshing,
    onManualRefresh: handleManualRefresh,
    onRetryLoadTasks: () => reloadTasks(),
    timelineTasks,
    dateObject: data.dateObject,
    displayedOperators,
    onTaskClick: taskActions.handleTaskClick,
    onTaskDrop: taskActions.handleTaskDrop,
    rollbackRef: taskActions.rollbackRef,
    scrollTriggerKey: data.scrollTriggerKey,
    conflictingTaskIds,
    activeConflicts,
    conflictsLoading,
    onConflictClick: handleConflictClick,
    onPrint: handlePrint,
    isPrinting,
    viewMode,
    onViewModeChange: handleViewModeChange,
    listPeriodType,
    listReferenceDate,
    onListNavigate: handleListNavigate,
    onListPeriodTypeChange: handleListPeriodTypeChange,
    listTasks,
    listLoading,
    listError,
    listIsRefreshing,
    onListRefresh: refreshListTasks,
    onListTaskClick: taskActions.handleTaskClick,
  };

  return (
    <div className={isDesktop ? "h-[calc(100vh-4rem)] overflow-hidden" : "pb-20"}>
      {isDesktop ? (
        <TeamViewDesktopLayout
          {...sharedLayoutProps}
          onDateChange={data.handleDateChange}
        />
      ) : (
        <TeamViewMobileLayout
          {...sharedLayoutProps}
          selectedDate={data.selectedDate}
          showCalendar={data.showCalendar}
          onToggleCalendar={data.toggleCalendar}
          onGoToToday={data.goToToday}
          onDateSelect={data.handleDateSelect}
        />
      )}

      <TaskDetailModal
        isOpen={taskActions.showTaskDetailModal}
        onClose={() => {
          taskActions.setShowTaskDetailModal(false);
          taskActions.setSelectedTaskForReassign(null);
        }}
        task={taskActions.selectedTaskForReassign}
        operator={taskActions.selectedTaskForReassign
          ? data.allProfilesForSite.find(p => p.id === taskActions.selectedTaskForReassign!.assigned_to_user_id) || null
          : null}
        serviceName={taskActions.selectedTaskForReassign?.template_service_id
          ? data.services.find(s => s.id === taskActions.selectedTaskForReassign!.template_service_id)?.name
          : undefined}
        serviceColor={taskActions.selectedTaskForReassign?.template_service_id
          ? data.services.find(s => s.id === taskActions.selectedTaskForReassign!.template_service_id)?.color
          : undefined}
        onEdit={taskActions.handleEditFromDetail}
        onDelete={taskActions.handleDeleteFromDetail}
        isAdmin={user.role === 'admin'}
        onStatusChange={taskActions.handleQuickStatusChange}
        onTextInputBatchSave={taskActions.handleQuickTextInputBatch}
      />

      <TaskEditModal
        isOpen={taskActions.showEditModal}
        onClose={() => {
          taskActions.setShowEditModal(false);
          taskActions.setDragTargetOperatorId(null);
          taskActions.setDragTargetTime(null);
          taskActions.rollbackRef.current?.();
        }}
        task={taskActions.selectedTaskForReassign}
        operators={data.allProfilesForSite}
        onReassign={taskActions.handleReassignTask}
        initialOperatorId={taskActions.dragTargetOperatorId || undefined}
        initialScheduledTime={taskActions.dragTargetTime || undefined}
        onSaveComplete={taskActions.handleSaveComplete}
      />

      <DeleteTaskModal
        isOpen={taskActions.showDeleteModal}
        onClose={() => {
          taskActions.setShowDeleteModal(false);
          taskActions.setSelectedTaskForDelete(null);
        }}
        task={taskActions.selectedTaskForReassign || taskActions.selectedTaskForDelete}
        onDelete={taskActions.handleDeleteTask}
      />
    </div>
  );
}
